// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@bg/App/Store/Reducers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import setDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetDrawerIsOpen";
import { getIsPunchOutSession, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const { fulfillmentMethod, isAuthenticated, rememberMe, isGuest, shipToId, pickUpWarehouse, properties } =
        getSession(state);
    const shipTo = getShipToState(state, shipToId).value;

    return {
        fulfillmentMethod,
        isAuthenticated,
        rememberMe,
        isGuest,
        shipTo,
        pickUpWarehouse,
        isDrawerOpen: state.components.addressDrawer.isOpen,
        isPunchOutSession: getIsPunchOutSession(state),
        resetAddress: state.commonActions["resetShipToAddress"],
        properties,
        customSettings: getSettingsCollection(state).customSettings as any,
    };
};

const mapDispatchToProps = {
    setDrawerIsOpen,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasShellContext;

export interface HeaderShipToAddressStyles {
    shipToAddressWrapper?: InjectableCss;
    titleTypography?: TypographyPresentationProps;
}

export const headerShipToAddressStyles: HeaderShipToAddressStyles = {
    shipToAddressWrapper: {
        css: css`
            display: flex;
            width: 100%;
            justify-content: flex-start;
            align-items: center;

            .name-phone-wrapper {
                font-size: 13px;
            }
        `,
    },
    titleTypography: {
        css: css`
            margin-right: 5px;
            font-weight: 500;
            font-size: 13px;
            line-height: 16px;
        `,
        ellipsis: true,
    },
};

interface OwnProps extends WidgetProps {
    fields: {};
}

const styles = headerShipToAddressStyles;

const HeaderShipToAddress: FC<Props> = ({ isAuthenticated, properties, customSettings }) => {
    let addressLabel = "";
    let phoneNumber = "";

    // BG-2223: Support Desk - Header Warehouse Display.
    const pickupWarehouseInfo = properties?.currentWarehouseInfo ? JSON.parse(properties?.currentWarehouseInfo) : {};
    if (pickupWarehouseInfo) {
        addressLabel = `${pickupWarehouseInfo.description || pickupWarehouseInfo.name}`;
        phoneNumber = pickupWarehouseInfo.phone;
    }

    // BG-2247: Header is assigning "Airdrie" Warehouse to Guest Users automatically
    if (
        customSettings &&
        customSettings.postalCodeLocationFinder.toLowerCase() === "true" &&
        typeof window !== "undefined" &&
        !window.localStorage.getItem("postalCodeID")?.length &&
        !isAuthenticated
    ) {
        return null;
    }

    return (
        <>
            <StyledWrapper {...styles.shipToAddressWrapper}>
                <Typography
                    size="13px"
                    lineHeight="16px"
                    color="common.white"
                    css={css`
                        font-weight: 500;
                        font-size: 13px;
                        line-height: 16px;
                    `}
                    data-test-selector="header_shipToLabel"
                    className="branch-name-lbl"
                >
                    {translate("Branch:")}&nbsp;
                </Typography>
                <StyledWrapper className="name-phone-wrapper">
                    <Typography
                        color="common.white"
                        {...styles.titleTypography}
                        data-test-selector="header_shipToLabel"
                    >
                        {addressLabel}
                    </Typography>
                    {isAuthenticated && phoneNumber && (
                        <a
                            href={`tel:${phoneNumber}`}
                            style={{ fontSize: "13px", lineHeight: "16px", color: "white", fontWeight: 500 }}
                            data-test-selector="header_shipToLabel"
                        >
                            <span>&nbsp;&nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;&nbsp;</span>
                            {phoneNumber}
                        </a>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(HeaderShipToAddress)),
    definition: {
        displayName: "Header Ship To Address Menu",
        icon: "location-dot",
        group: "XNGAGE" as any,
    },
};

export default widgetModule;
